import {
  isAxiosError,
  HttpStatusCode,
  AxiosHeaders,
  type AxiosInstance,
} from 'axios';
import { noop } from 'lodash-es';
import { getTurnstileSDK } from './sdk';

const WIDGET_ID = 'cf-turnstile-widget';
type TurnstileWidget = HTMLDivElement & {
  __challenge__: Promise<{ result: 'ok' | 'fail' }>;
  __interactive__: VoidFunction;
  __visible__?: boolean;
  __intreaction_required__?: boolean;
};

type WidgetOverlay = HTMLDivElement & {
  __visible__: VoidFunction;
  __invisible__: VoidFunction;
  __install__: () => VoidFunction;
};

interface ChallengeHookParams {
  intereactionRequired: boolean;
  visible: boolean;
}

function addInteractiveListener(widget: TurnstileWidget) {
  widget.__interactive__();
  widget.addEventListener('interactionRequired', widget.__interactive__);

  return () =>
    widget.removeEventListener('interactionRequired', widget.__interactive__);
}

function createWidgetOverlay(widget: TurnstileWidget) {
  const overlay = document.createElement('div') as WidgetOverlay;
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.appendChild(widget);

  overlay.__invisible__ = () => {
    widget.__visible__ = false;
    overlay.style.position = 'absolute';
    overlay.style.visibility = 'hidden';
    overlay.style.top = '-9999px';
    overlay.style.left = '-9999px';
    overlay.style.zIndex = '-9999';
  };

  overlay.__visible__ = () => {
    widget.__visible__ = true;
    overlay.style.position = 'fixed';
    overlay.style.visibility = 'visible';
    overlay.style.inset = '0';
    overlay.style.zIndex = '9999';
    overlay.style.background = 'rgba(0,0,0,0.5)';
  };

  overlay.__install__ = () => {
    overlay.__invisible__();
    document.body.appendChild(overlay);

    return () => document.body.removeChild(overlay);
  };

  return overlay;
}

interface ChallengeOptions {
  siteKey: string;
  onChallengeError?: (error: Error, params: ChallengeHookParams) => void;
  onChallengeSolved?: (params: ChallengeHookParams) => void;
  onInteractiveChallengePrompted?: VoidFunction;
  onWafForbiddenError?: (params: { isCleared: boolean }) => void;
}

export class CfTurnstileChallenge {
  readonly #siteKey: string;

  readonly #onChallengeError: (
    error: unknown,
    params: ChallengeHookParams,
  ) => void;

  readonly #onChallengeSolved: (params: ChallengeHookParams) => void;

  readonly #onInteractiveChallengePrompted: VoidFunction;

  readonly #onWafForbiddenError: (params: { isCleared: boolean }) => void;

  constructor(opts: ChallengeOptions) {
    this.#siteKey = opts.siteKey;
    this.#onChallengeError = opts.onChallengeError ?? noop;
    this.#onChallengeSolved = opts.onChallengeSolved ?? noop;
    this.#onInteractiveChallengePrompted =
      opts.onInteractiveChallengePrompted ?? noop;
    this.#onWafForbiddenError = opts.onWafForbiddenError ?? noop;
  }

  async initChallengeWidget() {
    const sdk = await getTurnstileSDK();

    const existingWidget = document.getElementById(
      WIDGET_ID,
    ) as TurnstileWidget | null;
    if (existingWidget) return existingWidget;

    const widget = document.createElement('div') as TurnstileWidget;
    widget.id = WIDGET_ID;

    const overlay = createWidgetOverlay(widget);

    widget.__interactive__ = () => {
      if (!widget.__intreaction_required__) return;

      this.#onInteractiveChallengePrompted();
      overlay.__visible__();
    };

    widget.__challenge__ = new Promise(resolve => {
      const cleanup = overlay.__install__();

      sdk.render(widget, {
        sitekey: this.#siteKey,
        callback: (_, preClearanceObtained) => {
          if (preClearanceObtained) {
            resolve({ result: 'ok' });
            this.#onChallengeSolved({
              visible: !!widget.__visible__,
              intereactionRequired: !!widget.__intreaction_required__,
            });
          } else {
            resolve({ result: 'fail' });
            this.#onChallengeError(
              new Error('failed to obtain crearance after challenge solve'),
              {
                visible: !!widget.__visible__,
                intereactionRequired: !!widget.__intreaction_required__,
              },
            );
          }
          sdk.remove(widget);
          cleanup();
        },
        'error-callback': errorCode => {
          resolve({ result: 'fail' });
          this.#onChallengeError(
            new Error(`challenge failed with error code: ${errorCode}`),
            {
              visible: !!widget.__visible__,
              intereactionRequired: !!widget.__intreaction_required__,
            },
          );
          cleanup();
        },
        'before-interactive-callback': () => {
          widget.__intreaction_required__ = true;
          widget.dispatchEvent(new Event('interactionRequired'));
        },
        appearance: 'always',
        theme: 'light',
        language: 'ru',
        retry: 'never',
      });
    });

    return widget;
  }

  async ensureChallengeSolved() {
    const challengeWidget = await this.initChallengeWidget();

    const cleanup = addInteractiveListener(challengeWidget);
    const ensure = await challengeWidget.__challenge__;
    cleanup();

    return ensure;
  }

  withAxiosInterceptors(http: AxiosInstance) {
    http.defaults.withCredentials = true;

    http.interceptors.response.use(null, async error => {
      if (!isAxiosError(error) || !error.response || !error.config) {
        return Promise.reject(error);
      }

      const { headers, status } = error.response;

      if (
        status === HttpStatusCode.Forbidden &&
        headers instanceof AxiosHeaders &&
        headers.has('cf-mitigated') &&
        headers.get('cf-mitigated') === 'challenge'
      ) {
        // @ts-expect-error custom property of error.config which is added for retried request
        if (error.config.__isClearedRetry__) {
          this.#onWafForbiddenError({ isCleared: true });
          return Promise.reject(error);
        }

        this.#onWafForbiddenError({ isCleared: false });

        const { result } = await this.ensureChallengeSolved();

        if (result === 'fail') {
          return Promise.reject(error);
        }

        // @ts-expect-error add custom property to not call infinite retries on failed challenge
        error.config.__isClearedRetry__ = true;
        return http.request(error.config);
      }

      return Promise.reject(error);
    });
  }
}
